/**
 * ImportExportService - Canonical import/export flows backed by UnifiedDataManager
 * date_of_change: 2025-10-18
 * rule_reference: AGENTS.md (SSOT) — imports create canonical entities in UDM; exports read canonical payloads
 */

import UnifiedDataManager from './UnifiedDataManager';
import { buildMonsterEntityFromRaw } from '../normalizers/MonsterEntityAdapter';

// Lazy import to avoid initial bundle bloat
async function normalize(payload, kind) {
  const { default: UnifiedDataNormalizer } = await import('../normalizers/UnifiedDataNormalizer');
  return UnifiedDataNormalizer.normalize(payload, kind);
}

const ImportExportService = {
  // Import a canonical character payload (or near-canonical) and persist into UDM
  async importCharacter(raw) {
    const { canonical, audit } = await normalize(raw, 'character');
    const now = new Date().toISOString();

    const pickArtworkKey = () => {
      const cand = (
        raw?.tokenAssetKey || raw?.tokenAsset?.key ||
        raw?.artwork || raw?.imageUrl ||
        raw?.data?.tokenAssetKey || raw?.data?.tokenAsset?.key || raw?.data?.artwork || raw?.data?.imageUrl ||
        raw?._meta?.tokenAssetKey || raw?._meta?.artwork ||
        canonical?.tokenAssetKey || canonical?.artwork || canonical?.imageUrl
      );
      if (!cand) return null;
      const s = String(cand).trim();
      if (!s) return null;
      // Normalize to 'uploads/...' without leading slash when applicable
      if (s.startsWith('/uploads/')) return s.slice(1);
      if (s.startsWith('uploads/')) return s;
      return s; // allow absolute URLs; resolver will handle
    };

    const tokenKey = pickArtworkKey();
    if (tokenKey && !canonical.tokenAssetKey) {
      canonical.tokenAssetKey = tokenKey;
    }

    const entity = {
      schemaVersion: '1.0.0',
      type: 'character',
      exportDate: now,
      id: canonical.id,
      source: { name: 'Upload', edition: '5e', license: 'Custom' },
      data: canonical,
      _meta: { createdAt: now, lastModified: now, audit, ...(tokenKey ? { artwork: tokenKey, tokenAssetKey: tokenKey } : {}) }
    };
    const saved = await UnifiedDataManager.saveEntity(entity, true);
    return saved;
  },

  // Import a canonical monster payload (or near-canonical) and persist into UDM
  async importMonster(raw) {
    const now = new Date().toISOString();

    // If the payload already looks like a canonical monster entity (monster_template.json),
    // accept its data block and normalize metadata/tags for uploads.
    const looksCanonicalEntity =
      raw && raw.type === 'monster' &&
      raw.data && typeof raw.data === 'object' &&
      raw.data.taxonomy && raw.data.defense && raw.data.movement && raw.data.challenge;

    let entity;

    if (looksCanonicalEntity) {
      const incomingMeta = (raw.metadata && typeof raw.metadata === 'object') ? raw.metadata : {};
      const tags = new Set([...(incomingMeta.tags || []), 'upload']);
      const metadata = {
        created: incomingMeta.created || now,
        modified: now,
        ...(tags.size ? { tags: Array.from(tags) } : {}),
        ...(incomingMeta.notes ? { notes: incomingMeta.notes } : {})
      };

      const fallbackId = raw.id || (raw.data.name ? String(raw.data.name).toLowerCase().replace(/[^a-z0-9]+/g, '-') : `monster_${Date.now()}`);

      entity = {
        schemaVersion: raw.schemaVersion || '1.0.0',
        type: 'monster',
        id: fallbackId,
        data: raw.data,
        metadata
      };
    } else {
      // Delegate SRD/legacy/plain shapes to MonsterEntityAdapter for strict schema alignment.
      const { id, data, metadata } = buildMonsterEntityFromRaw(raw, {
        now,
        // date_of_change: 2025-12-03 - Preserve incoming ID when present so editor/import flows
        // can round-trip monster identifiers into the canonical library.
        defaultId: (raw && typeof raw.id === 'string' && raw.id.trim().length)
          ? String(raw.id).trim()
          : undefined,
        markAsTemplate: false,
        markAsUpload: true
      });

      entity = {
        schemaVersion: '1.0.0',
        type: 'monster',
        id,
        data,
        metadata
      };
    }

    const saved = await UnifiedDataManager.saveEntity(entity, true);
    return saved;
  },

  // Export a character by id to a portable JSON document
  async exportCharacter(id) {
    const entity = await UnifiedDataManager.loadEntity(id, 'character');
    if (!entity) throw new Error(`Character not found: ${id}`);
    return {
      version: '1.0',
      type: 'character',
      exportDate: new Date().toISOString(),
      id: entity.id,
      data: entity.data,
      _meta: entity._meta || {}
    };
  },

  // Export a monster by id to a portable JSON document
  async exportMonster(id) {
    const entity = await UnifiedDataManager.loadEntity(id, 'monster');
    if (!entity) throw new Error(`Monster not found: ${id}`);
    return {
      version: '1.0',
      type: 'monster',
      exportDate: new Date().toISOString(),
      id: entity.id,
      data: entity.data,
      _meta: entity._meta || {}
    };
  }
};

export default ImportExportService;
